import { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { MotionFlex, MotionBox as Box, appearCTARight } from "utils/motion";
import FloatingCTAIcon from "components/icons/FloatingCTAIcon";
import Button from "components/base/Button";
import Trans from "components/base/Trans";

// Site-wide switch for the floating "I'm Interested" tab.
// Set to true to restore it on every page that mounts <FloatingCTA />.
const ENABLE_FLOATING_CTA = false;

const variants = {
  regular: { backgroundColor: "rgba(6, 88, 45, 0.70)" },
  hovered: { backgroundColor: "#0fb960" },
};
const arrowVariants = { regular: { opacity: 0 }, hovered: { opacity: 1 } };

function isBannerTime(targetTimeStr) {
  const targetTime = new Date(targetTimeStr);
  const currentTime = new Date();

  const hoursDifference = (targetTime - currentTime) / (1000 * 60 * 60);
  // Check if the difference is less than 3 hours but no more than 0.75 hours (45 minutes)
  return hoursDifference < 3 && hoursDifference > -0.75;
}

const FloatingBanner = dynamic(
  () => import("components/blocks/FloatingBanner"),
);

export default function FloatingCTA({ show, text, onClick, boxProps }) {
  const [isHovered, setHovered] = useState(false);
  const special = useMemo(() => isBannerTime(process.env.next_event_date), []);

  if (special) return <FloatingBanner show={show} />;

  if (!ENABLE_FLOATING_CTA) return null;

  return (
    <MotionFlex
      position="fixed"
      bottom="52px"
      right="0px"
      zIndex="100"
      variants={appearCTARight}
      initial="hidden"
      animate={show ? "visible" : "hidden"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...boxProps}
    >
      <Box
        variants={variants}
        initial="regular"
        animate={isHovered ? "hovered" : "regular"}
        bg="rgba(6, 88, 45, 0.80)"
        boxShadow="0 0 6px 0 rgba(0, 0, 0, 0.15)"
      >
        <Button
          variant="floating"
          inverted
          onClick={
            onClick ||
            (() => {
              document
                .getElementById("register")
                ?.scrollIntoView({ behavior: "smooth" });
            })
          }
          trackLabel="floating_im_interested"
        >
          {text || <Trans id="LBL_CTA_IMINTERESTED" />}
          <Box ml="30px" position="relative">
            <FloatingCTAIcon />
            <Box
              position="absolute"
              top={0}
              variants={arrowVariants}
              initial="regular"
              animate={isHovered ? "hovered" : "regular"}
            >
              <FloatingCTAIcon color="white" />
            </Box>
          </Box>
        </Button>
      </Box>
    </MotionFlex>
  );
}
